import { useCallback, useEffect, useState } from 'react'
import { ExternalLink, Play } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { formatWhen } from '../lib/format'
import type { CollectionOverview, CollectionRun } from '../types'

const running = (run: CollectionRun | null) => Boolean(run && run.status !== 'completed')

function runLabel(run: CollectionRun | null) {
  if (!run) return { text: 'Nenhuma coleta ainda', tone: 'deleted' }
  if (running(run)) return { text: run.status === 'in_progress' ? 'Rodando agora' : 'Na fila', tone: 'canceled' }
  if (run.conclusion === 'success') return { text: `Concluída · ${formatWhen(run.updated_at)}`, tone: 'published' }
  if (run.conclusion === 'skipped') return { text: `Desligada até a virada · ${formatWhen(run.updated_at)}`, tone: 'deleted' }
  return { text: `Falhou · ${formatWhen(run.updated_at)}`, tone: 'error' }
}

// The automatic collection's last run per source and an extra run on request
// (PRD.md, item 6), refreshed while one runs.
export function CollectionPanel({ canEdit, onSaved, onError }: {
  canEdit: boolean
  onSaved: (message: string) => void
  onError: (message: string) => void
}) {
  const [overview, setOverview] = useState<CollectionOverview | null>(null)
  const [requested, setRequested] = useState('')

  const load = useCallback(async () => {
    try {
      setOverview(await cmsApi.collectionRuns())
    } catch {
      setOverview({ configured: false, writes_enabled: false, can_run: false, sources: [] })
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const anyRunning = overview?.sources.some((item) => running(item.last_run)) || Boolean(requested)
  useEffect(() => {
    if (!anyRunning) return
    const timer = window.setInterval(() => void load(), 10_000)
    return () => window.clearInterval(timer)
  }, [anyRunning, load])
  useEffect(() => {
    if (requested && overview?.sources.some((item) => item.source === requested && running(item.last_run))) setRequested('')
  }, [overview, requested])

  const run = async (source: string, name: string) => {
    setRequested(source)
    try {
      await cmsApi.runCollection(source)
      onSaved(`Coleta da ${name} pedida. O status aparece aqui em alguns segundos.`)
      window.setTimeout(() => void load(), 4_000)
    } catch (error) {
      setRequested('')
      onError(error instanceof Error ? error.message : 'Não foi possível pedir a coleta.')
    }
  }

  if (!overview) return null
  const blocked = !overview.configured
    ? 'A coleta extra ainda não foi configurada: falta o acesso ao GitHub.'
    : !overview.writes_enabled ? 'A coleta automática grava a partir da virada para o Guve 2.0. Até lá, o botão fica desligado.' : ''

  return (
    <section className="panel panel--flush" aria-label="Coleta automática">
      <div className="panel__heading">
        <div><h2>Coleta automática</h2><p>{overview.can_run
          ? 'Os robôs leem as fontes nos horários fixos. Para trazer novidades antes, rode uma coleta extra.'
          : 'Os robôs leem as fontes nos horários fixos.'}</p></div>
      </div>
      <div className="collection-list">
        {overview.sources.map((item) => {
          const label = runLabel(item.last_run)
          const busy = requested === item.source || running(item.last_run)
          return (
            <article className="collection-row" key={item.source} aria-label={`Coleta: ${item.name}`}>
              <div className="collection-row__main">
                <strong>{item.name}</strong>
                <small>{item.schedule}</small>
              </div>
              <span className="collection-row__status">
                <span className={`status-pill status-pill--${label.tone}`}>{label.text}</span>
                {item.last_run && label.tone === 'error' && <a className="text-button" href={item.last_run.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Ver detalhes</a>}
              </span>
              {overview.can_run && (
                <button type="button" className="button button--secondary" disabled={!canEdit || Boolean(blocked) || busy} onClick={() => void run(item.source, item.name)}>
                  <Play size={15} /> {busy ? 'Rodando…' : 'Rodar coleta agora'}
                </button>
              )}
            </article>
          )
        })}
      </div>
      {blocked && overview.can_run && <p className="collection-note">{blocked}</p>}
    </section>
  )
}
